import { useState, useContext, useEffect } from "react";
import AddTestStepper from "../../components/add/AddTestStepper";
import { debounce } from "lodash";
import AddTestPageNavButtons from "./AddTestPageNavButtons";
import VideoCard from "../global/VideoCard";
import { VideoContext } from "../../context/video_context";
import { toast } from "sonner";
import LoadBox from "components/global/LoadBox";
import { Button } from "../ui/button";
import { RefreshCw } from "lucide-react";

type SelectVideosType = {
  handlePageChangeUp: any;
  handlePageChangeDown: any;
  handlePageChange: any;
};

const activeStep: number = 0;

export default function SelectVideos(fn: SelectVideosType) {
  const videoContext = useContext(VideoContext);
  const videos = videoContext?.videos || [];
  const loading = videoContext?.loading || false;
  const getVideos = videoContext?.getVideos || (() => Promise.resolve({ videos: [], count: 0 }));
  const updateVideosFromChannel =
    videoContext?.updateVideosFromChannel || (() => Promise.resolve({}));
  const results_per_page = videoContext?.results_per_page || 20;
  const [search, setSearch] = useState<string>("");
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [count, setCount] = useState<number>(0);
  const [height, setHeight] = useState("60vh");
  const totalPages = Math.ceil(count / results_per_page);

  useEffect(() => {
    toast.promise(
      updateVideosFromChannel().then(() => {
        console.log("Ran updateVideosFromChannel");
      }),
      {
        loading: "Checking for new videos...",
        success: "Video list updated from channel.",
        error: "Error updating videos from channel.",
      }
    );
  }, []);

  const searchVideos = (search: string, currentPage: number) => {
    getVideos(search, currentPage).then(({ videos, count }: any) => {
      setCount(count);
    });
  };
  const debouncedSearch = debounce(searchVideos, 750);

  useEffect(() => {
    debouncedSearch(search, currentPage);
  }, [search, currentPage]);

  useEffect(() => {
    const updateHeight = () => setHeight(`${window.innerHeight * 0.7}px`);
    updateHeight();
    window.addEventListener("resize", updateHeight);
    return () => window.removeEventListener("resize", updateHeight);
  }, []);

  return (
    <div className="w-full pt-4 flex flex-col h-screen">
      <AddTestStepper activeStep={activeStep} />
      <div className="px-6 pt-4 w-full">
        <h3 className="text-xl font-semibold mt-4 mb-3 text-foreground">Videos</h3>
        <div className="flex gap-2 mb-4">
          <input
            className="flex-1 px-3 py-2 text-sm rounded-md border border-input bg-background text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search your videos"
          />
          <Button
            onClick={() => {
              toast.promise(
                updateVideosFromChannel().then(() => searchVideos(search, currentPage)),
                {
                  loading: "Updating Videos...",
                  success: "Videos Updated",
                  error: "Error Updating Videos",
                }
              );
            }}
            className="shrink-0 gap-2"
          >
            <RefreshCw className="h-4 w-4" />
            Update from Channel
          </Button>
        </div>
        <LoadBox loading={loading}>
          <div className="overflow-auto" style={{ height }}>
            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-2">
              {videos.map((video) => (
                <div key={video.video_id} className="min-w-[280px]">
                  <VideoCard
                    {...video}
                    views={video.views || "0"}
                    uploaded={video.uploaded || new Date()}
                    handlePageChangeUp={fn.handlePageChangeUp}
                  />
                </div>
              ))}
            </div>
          </div>
          {totalPages > 1 && (
            <div className="flex justify-center gap-2 mt-4 mb-2">
              {Array.from({ length: totalPages }, (_, i) => i + 1).map((page) => (
                <Button
                  key={page}
                  variant={currentPage === page ? "default" : "outline"}
                  size="sm"
                  onClick={() => setCurrentPage(page)}
                >
                  {page}
                </Button>
              ))}
            </div>
          )}
        </LoadBox>
      </div>
    </div>
  );
}
